import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientAPI } from '../services/api';

function PatientList() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchPatients();
  }, [searchTerm, currentPage]);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const response = await patientAPI.getAll({
        search: searchTerm,
        page: currentPage,
        limit: 10
      });

      setPatients(response.data.data);
      setTotalPages(response.data.totalPages);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch patients');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  const handleViewDetails = (patientId) => {
    navigate(`/patient/${patientId}`);
  };

  const handleDelete = async (patientId) => {
    if (window.confirm('Are you sure you want to delete this patient?')) {
      try {
        await patientAPI.delete(patientId);
        fetchPatients();
      } catch (err) {
        alert(err.response?.data?.error || 'Failed to delete patient');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  return (
    <div className="card">
      <h2>👥 Patient List</h2>

      <div className="search-box">
        <input
          type="text"
          placeholder="Search by patient ID, name, phone, or email..."
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>

      {error && <div className="error-message">❌ {error}</div>}

      {loading ? (
        <div className="loading">Loading patients...</div>
      ) : patients.length === 0 ? (
        <div>
          {searchTerm ? 'No patients found matching your search.' : 'No patients registered yet.'}
        </div>
      ) : (
        <>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Patient ID</th>
                  <th>Name</th>
                  <th>Age</th>
                  <th>Gender</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Registered On</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {patients.map(patient => (
                  <tr key={patient.id} onClick={() => handleViewDetails(patient.id)} style={{ cursor: 'pointer' }}>
                    <td><strong>{patient.patientId || '-'}</strong></td>
                    <td>{patient.firstName} {patient.lastName}</td>
                    <td>{calculateAge(patient.dateOfBirth)} years</td>
                    <td>{patient.gender}</td>
                    <td>{patient.phone}</td>
                    <td>{patient.email || '-'}</td>
                    <td>{formatDate(patient.createdAt)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn btn-primary"
                        onClick={() => handleViewDetails(patient.id)}
                        style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                      >
                        View Details
                      </button>
                      {/* <button
                        className="btn btn-danger"
                        onClick={() => handleDelete(patient.id)}
                        style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                      >
                        Delete
                      </button> */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      <div style={{ marginTop: '2rem' }}>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/')}
        >
          Add New Patient
        </button>
      </div>
    </div>
  );
}

export default PatientList;
